import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * 通用 Pill Tabs 切換條
 *
 * 支援兩種模式：
 * 1) Route 模式：傳入 items=[{ key, label, path, icon? }]，自動使用 react-router 導航。
 * 2) Controlled 模式：傳入 items=[{ key, label, icon? }] + activeKey + onChange。
 *
 * 視覺：膠囊（pill）外殼 + 內部分頁，選中分頁變為主色填滿。
 *
 * RWD：
 * - >=769px：置中，最大寬度限制，外殼有陰影。
 * - <768px：可水平捲動，背景色淡，自動 scroll 到目前選中項。
 */

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 24px 4px;

  @media (max-width: 768px) {
    padding: 12px 12px 0;
    justify-content: stretch;
  }
`

const Bar = styled.div`
  display: inline-flex;
  background: #f4f5f7;
  border-radius: 999px;
  padding: 4px;
  gap: 2px;
  box-shadow: inset 0 0 0 1px rgba(15, 37, 98, 0.04);
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  max-width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 999px;
  }
`

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  padding: 8px 18px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  background: transparent;
  white-space: nowrap;
  transition: all 0.18s ease;
  min-width: 88px;
  outline: none;

  &:hover {
    color: ${(p) => p.$color};
  }

  ${(p) =>
    p.$active &&
    `
    background: ${p.$color};
    color: #fff;
    box-shadow: 0 4px 10px ${p.$color}40;

    &:hover {
      color: #fff;
    }
  `}

  .anticon, svg {
    font-size: 15px;
  }

  /* 手機版：不要 flex:1 硬擠等寬（會塞爆、圖示疊到文字）。
     改為自然寬度 + 水平捲動（Bar 已有 overflow-x:auto 與自動捲到選中項）。 */
  @media (max-width: 768px) {
    flex: 0 0 auto;
    min-width: 0;
    padding: 7px 14px;
    font-size: 13px;
    gap: 5px;

    .anticon,
    svg {
      font-size: 14px;
      flex-shrink: 0;
    }
  }
`

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: ${(p) => (p.$active ? 'rgba(255,255,255,0.25)' : '#e5e7eb')};
  color: ${(p) => (p.$active ? '#fff' : '#6b7280')};
  font-size: 11px;
  font-weight: 700;
  margin-left: 2px;
`

const DEFAULT_COLOR = '#d5728a'

const PillTabs = ({
  items = [],
  activeKey,
  onChange,
  color = DEFAULT_COLOR,
  className
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const barRef = useRef(null)

  const routeMode = items.some((i) => !!i.path)

  // 計算目前的 active key（route 模式優先 match path）
  const currentKey = routeMode
    ? items.find((i) => location.pathname.includes(i.path))?.key ||
      items[0]?.key
    : activeKey

  // 自動 scroll 到目前選中項（僅水平捲動 bar 容器，不影響整頁）
  // 注意：不使用 element.scrollIntoView，否則初次掛載時瀏覽器會把整頁往下捲
  // 以便讓元素出現在視野中。改為直接設定 bar.scrollLeft。
  useEffect(() => {
    if (!barRef.current) return
    const bar = barRef.current
    const activeEl = bar.querySelector(`[data-tabkey="${currentKey}"]`)
    if (!activeEl) return

    const isOverflow = bar.scrollWidth > bar.clientWidth
    if (!isOverflow) return // 內容不需捲動就不操作

    const elLeft = activeEl.offsetLeft
    const elWidth = activeEl.offsetWidth
    const targetLeft = Math.max(
      0,
      elLeft - (bar.clientWidth - elWidth) / 2
    )

    if (Math.abs(bar.scrollLeft - targetLeft) < 2) return // 已對齊就不動
    try {
      bar.scrollTo({ left: targetLeft, behavior: 'smooth' })
    } catch (e) {
      bar.scrollLeft = targetLeft
    }
  }, [currentKey])

  const handleClick = (item) => {
    if (item.disabled) return
    if (routeMode && item.path) {
      navigate(item.path)
    }
    if (onChange) onChange(item.key)
  }

  return (
    <Wrapper className={className}>
      <Bar ref={barRef} role='tablist'>
        {items.map((item) => {
          const active = currentKey === item.key
          return (
            <Tab
              key={item.key}
              data-tabkey={item.key}
              role='tab'
              aria-selected={active}
              $active={active}
              $color={color}
              onClick={() => handleClick(item)}
              disabled={item.disabled}
            >
              {item.icon}
              <span>{item.label}</span>
              {typeof item.count === 'number' && (
                <CountBadge $active={active}>{item.count}</CountBadge>
              )}
            </Tab>
          )
        })}
      </Bar>
    </Wrapper>
  )
}

export default PillTabs
